import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { buildSelfServiceApiUrl, unwrapArray, unwrapObject } from './self-service-api.utils';

export interface ComputationLibraryEntry {
  id: string;
  kind: 'column' | 'aggregation';
  name: string;
  label?: string;
  expression: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  versions: Array<{ expression: string; label?: string; savedAt: string }>;
}

@Injectable({
  providedIn: 'root',
})
export class SelfServiceComputationLibraryService {
  private static readonly STORAGE_KEY = 'dvm.selfService.computationLibrary';

  constructor(private readonly http: HttpClient) {}

  list(): Observable<ComputationLibraryEntry[]> {
    return this.http
      .get<unknown>(buildSelfServiceApiUrl('/api/dvm/self-service/computation-library'))
      .pipe(
        map((response) => {
          const items = unwrapArray<ComputationLibraryEntry>(response, ['items', 'data', 'results']);
          return items.length ? items : this.readLocal();
        }),
        catchError(() => of(this.readLocal())),
      );
  }

  create(entry: ComputationLibraryEntry): Observable<ComputationLibraryEntry> {
    return this.http
      .post<unknown>(
        buildSelfServiceApiUrl('/api/dvm/self-service/computation-library'),
        entry,
      )
      .pipe(
        map((response) => unwrapObject<ComputationLibraryEntry>(response) || entry),
        catchError(() => {
          const next = [entry, ...this.readLocal().filter((item) => item.id !== entry.id)];
          this.writeLocal(next);
          return of(entry);
        }),
      );
  }

  update(entry: ComputationLibraryEntry): Observable<ComputationLibraryEntry> {
    return this.http
      .put<unknown>(
        buildSelfServiceApiUrl(`/api/dvm/self-service/computation-library/${encodeURIComponent(entry.id)}`),
        entry,
      )
      .pipe(
        map((response) => unwrapObject<ComputationLibraryEntry>(response) || entry),
        catchError(() => {
          const next = this.readLocal().map((item) => (item.id === entry.id ? entry : item));
          this.writeLocal(next);
          return of(entry);
        }),
      );
  }

  delete(entryId: string): Observable<void> {
    return this.http
      .delete<void>(
        buildSelfServiceApiUrl(`/api/dvm/self-service/computation-library/${encodeURIComponent(entryId)}`),
      )
      .pipe(
        catchError(() => {
          this.writeLocal(this.readLocal().filter((item) => item.id !== entryId));
          return of(void 0);
        }),
      );
  }

  restoreVersion(entryId: string, versionIndex: number): Observable<ComputationLibraryEntry | null> {
    return this.http
      .post<unknown>(
        buildSelfServiceApiUrl(
          `/api/dvm/self-service/computation-library/${encodeURIComponent(entryId)}/versions/${versionIndex}/restore`,
        ),
        {},
      )
      .pipe(
        map((response) => unwrapObject<ComputationLibraryEntry>(response)),
        catchError(() => of(null)),
      );
  }

  private readLocal(): ComputationLibraryEntry[] {
    try {
      const raw = window.localStorage.getItem(SelfServiceComputationLibraryService.STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private writeLocal(entries: ComputationLibraryEntry[]): void {
    try {
      window.localStorage.setItem(
        SelfServiceComputationLibraryService.STORAGE_KEY,
        JSON.stringify(entries),
      );
    } catch {
      // Best-effort fallback only.
    }
  }
}
